import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Send } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import SEO from "@/components/SEO";
import { getTemplate } from "@/lib/applicationTemplates";

const initialAnswers = {
  age: "",
  country: "",
  timezone: "",
  hours_per_week: "",
  active_days: "",
  moderation_experience: "",
  community_experience: "",
  links: "",
  why_join: "",
  why_choose: "",
  what_improve: "",
  about_yourself: "",
};

/**
 * Application form for a single position. Discord username/id are auto-filled
 * from the user session and NOT editable — protects against typos/fraud.
 */
export default function ApplyForm() {
  const { positionId } = useParams();
  const { user, loginWithDiscord, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [position, setPosition] = useState(null);
  const [answers, setAnswers] = useState(initialAnswers);
  const [scenarios, setScenarios] = useState({});
  const [truthful, setTruthful] = useState(false);
  const [agrees, setAgrees] = useState(false);
  const [busy, setBusy] = useState(false);
  const [loadingPos, setLoadingPos] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/positions/${positionId}`);
        setPosition(data);
      } catch {
        toast.error("Position not found or closed");
        navigate("/apply", { replace: true });
      } finally {
        setLoadingPos(false);
      }
    })();
  }, [positionId, navigate]);

  const set = (field) => (e) => setAnswers((a) => ({ ...a, [field]: e.target.value }));
  const setScenario = (id) => (e) =>
    setScenarios((s) => ({ ...s, [id]: e.target.value }));

  const template = getTemplate(position?.questions_template);

  const submit = async (e) => {
    e.preventDefault();
    if (!user) return loginWithDiscord();
    if (!truthful || !agrees) {
      toast.error("Please tick both agreement boxes before submitting.");
      return;
    }
    // Client-side check that every scenario in the active template is filled.
    for (const q of template.questions) {
      const v = (scenarios[q.id] || "").trim();
      if (v.length < (q.minLength || 10)) {
        toast.error(`Please answer: "${q.label}"`);
        return;
      }
    }
    setBusy(true);
    try {
      await api.post("/applications", {
        position_id: positionId,
        ...answers,
        scenarios,
        truthful,
        agrees_guidelines: agrees,
      });
      toast.success("Application submitted! We'll reach out on Discord if you're selected.");
      navigate("/apply", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to submit application");
    } finally {
      setBusy(false);
    }
  };

  if (loadingPos || authLoading) {
    return <div className="max-w-3xl mx-auto px-4 py-16 text-zinc-500">Loading…</div>;
  }
  if (!position) return null;

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 pt-24 pb-16" data-testid="apply-form-page">
      <SEO
        title={`Apply — ${position.name} · Serverlistr`}
        description={`Apply for the ${position.name} role on Serverlistr's staff team.`}
        canonical={`/apply/${positionId}`}
        noindex
      />

      <Link to="/apply" className="inline-flex items-center gap-1 text-sm text-zinc-400 hover:text-white mb-4">
        <ArrowLeft className="w-4 h-4" /> All positions
      </Link>

      <h1 className="font-heading text-3xl sm:text-4xl font-black">{position.name}</h1>
      <p className="mt-2 text-zinc-400">{position.description}</p>

      {!user && (
        <div className="mt-6 rounded-xl border border-blurple/40 bg-blurple/10 p-4 text-sm">
          You need to <button onClick={loginWithDiscord} className="underline font-semibold">sign in with Discord</button> to apply — we auto-fill your Discord identity so we can verify it.
        </div>
      )}

      <form onSubmit={submit} className="mt-8 space-y-8" data-testid="apply-form">
        <Section title="Basic Information">
          <ReadOnly label="Discord Username" value={user?.discord_username || user?.name || "—"} testId="ro-discord-username" />
          <ReadOnly label="Discord User ID" value={user?.discord_id || "—"} testId="ro-discord-id" />
          <Field label="Age (optional)" value={answers.age} onChange={set("age")} placeholder="e.g. 21" />
          <Field label="Country" required value={answers.country} onChange={set("country")} placeholder="e.g. United States" />
          <Field label="Time Zone" required value={answers.timezone} onChange={set("timezone")} placeholder="e.g. EST / UTC-5" />
        </Section>

        <Section title="Availability">
          <Field label="Hours available per week" required value={answers.hours_per_week} onChange={set("hours_per_week")} placeholder="e.g. 10-15" />
          <Field label="Typical active days" required value={answers.active_days} onChange={set("active_days")} placeholder="e.g. Weeknights + weekends" />
        </Section>

        <Section title="Experience">
          <TextArea label="Previous moderation experience" value={answers.moderation_experience} onChange={set("moderation_experience")} placeholder="Describe any past moderation roles you've held." />
          <TextArea label="Previous community management experience" value={answers.community_experience} onChange={set("community_experience")} placeholder="Community management, event running, etc." />
          <Field label="Links (optional)" value={answers.links} onChange={set("links")} placeholder="Portfolio / references / past servers" />
        </Section>

        <Section title="Motivation">
          <TextArea required minLength={10} label="Why do you want to join Serverlistr?" value={answers.why_join} onChange={set("why_join")} />
          <TextArea required minLength={10} label="Why should we choose you?" value={answers.why_choose} onChange={set("why_choose")} />
          <TextArea required label="What would you improve about Serverlistr?" value={answers.what_improve} onChange={set("what_improve")} />
          <TextArea required minLength={10} label="Tell us about yourself." value={answers.about_yourself} onChange={set("about_yourself")} />
        </Section>

        {template.questions.length > 0 && (
          <Section title={template.id === "moderation" ? "Scenario Questions" : `${template.label} — Role-specific Questions`}>
            {template.questions.map((q) => (
              <TextArea
                key={q.id}
                required
                minLength={q.minLength || 10}
                label={q.label}
                placeholder={q.placeholder}
                value={scenarios[q.id] || ""}
                onChange={setScenario(q.id)}
              />
            ))}
          </Section>
        )}

        <Section title="Agreement">
          <label className="flex items-start gap-3 text-sm">
            <input
              type="checkbox"
              checked={truthful}
              onChange={(e) => setTruthful(e.target.checked)}
              data-testid="agree-truthful"
              className="mt-0.5 w-4 h-4 accent-blurple"
            />
            <span>All information I've provided above is truthful.</span>
          </label>
          <label className="flex items-start gap-3 text-sm">
            <input
              type="checkbox"
              checked={agrees}
              onChange={(e) => setAgrees(e.target.checked)}
              data-testid="agree-guidelines"
              className="mt-0.5 w-4 h-4 accent-blurple"
            />
            <span>I agree to follow the Serverlistr Staff Guidelines if accepted.</span>
          </label>
        </Section>

        <button
          type="submit"
          disabled={busy || !user || !truthful || !agrees}
          data-testid="submit-application-btn"
          className="inline-flex items-center gap-2 h-12 px-6 rounded-xl bg-blurple hover:bg-blurple-hover text-white font-bold transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-blurple/20 active:scale-95"
        >
          <Send className="w-4 h-4" />
          {busy ? "Submitting…" : "Submit application"}
        </button>
      </form>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <fieldset className="rounded-2xl border border-white/10 bg-[#0A0A0F] p-6 space-y-4">
      <legend className="px-2 font-heading text-sm font-black uppercase tracking-widest text-zinc-400">
        {title}
      </legend>
      {children}
    </fieldset>
  );
}
function Field({ label, required, value, onChange, placeholder, minLength }) {
  return (
    <label className="block">
      <div className="text-xs font-semibold text-zinc-400 mb-1">{label}{required && <span className="text-red-400"> *</span>}</div>
      <input
        type="text"
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        required={required}
        minLength={minLength}
        className="w-full h-11 px-3 bg-[#12121A] border border-white/10 rounded-lg text-sm placeholder-zinc-600 focus:border-blurple focus:outline-none focus:ring-1 focus:ring-blurple"
      />
    </label>
  );
}
function TextArea({ label, required, value, onChange, placeholder, minLength }) {
  return (
    <label className="block">
      <div className="text-xs font-semibold text-zinc-400 mb-1">{label}{required && <span className="text-red-400"> *</span>}</div>
      <textarea
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        required={required}
        minLength={minLength}
        rows={4}
        className="w-full px-3 py-2 bg-[#12121A] border border-white/10 rounded-lg text-sm placeholder-zinc-600 focus:border-blurple focus:outline-none focus:ring-1 focus:ring-blurple resize-y"
      />
    </label>
  );
}
function ReadOnly({ label, value, testId }) {
  return (
    <div>
      <div className="text-xs font-semibold text-zinc-400 mb-1">{label}</div>
      <div
        data-testid={testId}
        className="w-full h-11 px-3 flex items-center bg-white/[0.03] border border-white/5 rounded-lg text-sm text-zinc-300"
      >
        {value}
      </div>
    </div>
  );
}
